import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  TextField,
  Button,
  MenuItem,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import { useFormContext } from "./FormContext";
import { FinalData } from "@/types/onboarding";

const stateList = [
  { id: "1", name: "Andhra Pradesh" },
  { id: "2", name: "Arunachal Pradesh" },
  { id: "3", name: "Assam" },
  { id: "4", name: "Bihar" },
  { id: "5", name: "Chhattisgarh" },
  { id: "29", name: "Chandigarh" },
  { id: "30", name: "Delhi" },
  { id: "6", name: "Goa" },
  { id: "7", name: "Gujarat" },
  { id: "8", name: "Haryana" },
  { id: "9", name: "Himachal Pradesh" },
  { id: "32", name: "Jammu and Kashmir" },
  { id: "10", name: "Jharkhand" },
  { id: "11", name: "Karnataka" },
  { id: "12", name: "Kerala" },
  { id: "31", name: "Ladakh" },
  { id: "13", name: "Madhya Pradesh" },
  { id: "14", name: "Maharashtra" },
  { id: "15", name: "Manipur" },
  { id: "16", name: "Meghalaya" },
  { id: "17", name: "Mizoram" },
  { id: "18", name: "Nagaland" },
  { id: "19", name: "Odisha" },
  { id: "20", name: "Punjab" },
  { id: "21", name: "Rajasthan" },
  { id: "22", name: "Sikkim" },
  { id: "23", name: "Tamil Nadu" },
  { id: "24", name: "Telangana" },
  { id: "25", name: "Tripura" },
  { id: "26", name: "Uttar Pradesh" },
  { id: "27", name: "Uttarakhand" },
  { id: "28", name: "West Bengal" },
];

const AddressDetails = ({
  onNext,
  onBack,
}: {
  onNext: () => void;
  onBack: () => void;
}) => {
  const { finalData, setFinalData } = useFormContext();
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors,isValid },
    setValue,
    watch,
  } = useForm({
    defaultValues: finalData,
    mode: "onChange",
  });

  // Watch for pincode changes
  const pinCode = watch("pin_code");
  const selectedState = watch("state_id");

  // Fetch city, district, and state based on pincode
  useEffect(() => {
    if (pinCode && pinCode.length === 6) {
      setLoading(true);
      fetch(`https://api.postalpincode.in/pincode/${pinCode}`)
        .then((res) => res.json())
        .then((data) => {
          if (data[0]?.Status === "Success") {
            const { State, District, Division } = data[0].PostOffice[0];

            const matchedState = stateList.find(
              (s) => s.name.toLowerCase() === State.toLowerCase()
            );

            setTimeout(() => {
              setValue("location", Division);
              setValue("district", District);
              if (matchedState) {
                setValue("state_id", matchedState.id.toString(), {
                  shouldValidate: true,
                  shouldDirty: true,
                  shouldTouch: true,
                });
              } else {
                console.warn("State not found:", State);
              }
            }, 0);
          } else {
            console.warn("Invalid Pincode");
          }
        })
        .catch(() => console.error("Failed to fetch pincode details"))
        .finally(() => setLoading(false));
    }
  }, [pinCode, setValue]);

  const onSubmit = (data: FinalData) => {
    setFinalData(data); // Save data globally
    onNext(); // Proceed to next step
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="w-full max-h-full space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Address Details</h2>
      <span className="text-gray-500 text-sm tracking-wide">
        Please provide your business address information
      </span>

      {/* Business Name */}
      <TextField
        label="Business Name"
        variant="outlined"
        fullWidth
        {...register("business_name", {
          required: "Business Name is required",
        })}
        error={!!errors.business_name}
        helperText={errors.business_name?.message}
      />

      {/* Address Line 1 */}
      <TextField
        label="Address Line 1"
        variant="outlined"
        fullWidth
        {...register("address_line_1", {
          required: "Address Line 1 is required",
        })}
        error={!!errors.address_line_1}
        helperText={errors.address_line_1?.message}
      />

      {/* Address Line 2 */}
      <TextField
        label="Address Line 2"
        variant="outlined"
        fullWidth
        {...register("address_line_2")}
      />

      <div className="grid grid-cols-2 gap-4">
        {/* Pincode */}
        <TextField
          label="Pincode"
          variant="outlined"
          fullWidth
          {...register("pin_code", {
            required: "Pincode is required",
            minLength: 6,
            maxLength: 6,
          })}
          error={!!errors.pin_code}
          helperText={errors.pin_code?.message}
          InputProps={{
            inputMode: "numeric",
            endAdornment: loading ? (
              <InputAdornment position="end">
                <CircularProgress size={20} sx={{color:"var(--color-blue)"}}/>
              </InputAdornment>
            ) : null,
          }}
          inputProps={{
            maxLength: 6,
          }}
          onKeyPress={(e) => {
            // Allow only numbers (0-9) and backspace (8)
            if (!/[0-9\b]/.test(e.key)) {
              e.preventDefault();
            }
          }}
        />
        <TextField
          label="City"
          variant="outlined"
          fullWidth
          {...register("location", { required: "City is required" })}
          error={!!errors.location}
          helperText={errors.location?.message}
          InputLabelProps={{ shrink: true }}
          disabled={loading}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <TextField
          label="District"
          variant="outlined"
          fullWidth
          {...register("district", { required: "District is required" })}
          error={!!errors.district}
          helperText={errors.district?.message}
          InputLabelProps={{ shrink: true }}
          disabled={loading}
        />
        {/* State Select */}
        <TextField
          select
          label="State"
          variant="outlined"
          fullWidth
          {...register("state_id", { required: "State is required" })}
          value={selectedState}
          error={!!errors.state_id}
          helperText={errors.state_id?.message}
          InputLabelProps={{ shrink: true }}
          disabled={loading}
        >
          {stateList.map((state) => (
            <MenuItem key={state.id} value={state.id.toString()}>
              {state.name}
            </MenuItem>
          ))}
        </TextField>
      </div>

      {/* Back & Next Buttons */}
      <div className="flex gap-4">
        <Button
          variant="outlined"
          color="primary"
          fullWidth
          onClick={onBack}
          sx={{
            borderColor: "var(--color-blue)",
            color: "var(--color-blue)",
          }}
        >
          Back
        </Button>
        <Button
          type="submit"
          variant="contained"
          fullWidth
          color="primary"
          disabled={loading || !isValid}
          sx={{
            backgroundColor: "var(--color-blue)",
          }}
        >
          {loading ? "Loading..." : "Next Step"}
        </Button>
      </div>
    </form>
  );
};

export default AddressDetails;
